import React, { useState, useEffect, Fragment, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Content } from 'styles/globalStyles';
import { TitleBar } from 'styles/layout/titlebar';
import { websitesSelector } from 'store/website/websiteSelectors';
import ReportService from 'services/report';
import WPSDataTable from 'components/wpstaq/WPSDataTable/WPSDataTable';
import StringHelper from 'helpers/string';
import JsxHelper from 'helpers/jsx';
import TableHelper from 'helpers/table';
import PackageHelper from 'helpers/package';
import WebsiteHelper from 'helpers/website';
import UserHelper from 'helpers/user';
import ReportTraits from '../ReportTraits';
import { reportsSettingsSelector } from 'store/setting/settingSelectors';
import { updateSingleReportSettings } from 'store/setting/settingActions';
import 'components/package/packages.css';

const AvailablePackageUpdates = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const allWebsites = useSelector(websitesSelector);
  const reportsSettings = useSelector(reportsSettingsSelector);
  const globalReportSettings = reportsSettings.available_package_updates || {};
  const [modal, setModal] = useState(false);
  const BREADCRUMBS = JsxHelper.createBreadcrumbs('Available Package Updates', 'reports');
  const mounted = useRef(true)

  useEffect(() => {
    fetchPackages();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line
  }, []);

  // ---------------------------------------
  // API CALLS
  // ---------------------------------------

  const fetchPackages = () => {
    const data = {
        limit: 1000,
        search: null,
        updatable_only: true,
    }
    setLoading(true);
    ReportService.filterAppsInstalledPacakges(data)
      .then((res) => {
        setTableData(res.map(item => {
          item.key = StringHelper.randomSlug();
          item.latest_version = item.update || null;
          item.latest_version_url = item.update_url || null;
          return item;
        }));
      })
      .finally(() => setLoading(false));
  }

  const saveSettings = () =>
    ReportTraits.saveEmailAlertSettings(dispatch, 'available_package_updates', modal, setModal, updateSingleReportSettings);

  // ---------------------------------------
  // TABLE DATA
  // ---------------------------------------

  const headers = [
    PackageHelper.renderScopeHeader(),
    {
      name: 'Website',
      selector: 'website',
      searchable: true,
      width: '27%',
      cell: (row) => {
        const website = allWebsites.find(w => w.slug === row.website_slug);
        if (website) {
          TableHelper.customizeCellValue(row, 'website', WebsiteHelper.getLabel(website) + ' ' + website.default_domain);
        }
        return JsxHelper.createTableWebsiteCell({
          website,
          paddingRight: '10px',
        })
      }
    },
    PackageHelper.renderDisplayNameHeader('Package', '32%'),
    {
      name: 'Version Update',
      selector: 'version',
      searchable: true,
      width: '15%',
      cell: (row) => JsxHelper.createTableCellFromTo({
        from: row.version,
        to: row.latest_version,
        icon: !row.latest_version_url ? 'warning' : null,
        iconColor: !row.latest_version_url ? 'warning' : null,
        tooltip: !row.latest_version_url ? 'The new version can only be installed from the WordPress dashboard.' : null,
      }),
    },
  ];

  // Note: This is a workaround to prevent the table from re-rendering when the modal is being edited.
  const renderTable = useMemo(() => {
    return (
      <WPSDataTable
        customClass='package-history-table'
        columns={headers}
        body={tableData}
        loading={loading}
        rowsPerPage={100}
        dataKey={'key'}
      />
    );
    // eslint-disable-next-line
  }, [tableData, loading]);

  return (
    <Fragment>
      <TitleBar>
        <TitleBar.Title breadcrumbs={BREADCRUMBS}>Available Packages Updates</TitleBar.Title>
        <TitleBar.Actions>
          {UserHelper.isPartnerOrEmployee() && (JsxHelper.createButton({
            label: 'Email Alerts',
            onClick: () => setModal({ name: 'alerts', cc_emails: (globalReportSettings.cc_emails || []).join(', ') }),
          }))}
          {JsxHelper.createBackButton('/reports')}
        </TitleBar.Actions>
      </TitleBar>
      <Content>
        {renderTable}
      </Content>
      {ReportTraits.extraEmailsModal(modal, setModal, saveSettings)}
    </Fragment>
  );
}

export default AvailablePackageUpdates;
